import React, { useState, useEffect, useRef } from 'react';

const AdminMedia = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });
  const [selectedImages, setSelectedImages] = useState([]);
  const [viewingIndex, setViewingIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });
  const fileInputRef = useRef(null);

  const filteredImages = images.filter(img => 
    img.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Lightbox keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (viewingIndex === null) return;
      if (e.key === 'Escape') setViewingIndex(null);
      if (e.key === 'ArrowLeft') handlePrevImage();
      if (e.key === 'ArrowRight') handleNextImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewingIndex, filteredImages.length]);

  const handlePrevImage = () => {
    setViewingIndex(prev => (prev > 0 ? prev - 1 : filteredImages.length - 1));
  };

  const handleNextImage = () => {
    setViewingIndex(prev => (prev < filteredImages.length - 1 ? prev + 1 : 0));
  };

  const showAlert = (title, message) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm: null });
  };

  const showConfirm = (title, message, onConfirm) => {
    setDialog({ isOpen: true, type: 'confirm', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin/login';
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch media');
      const data = await response.json();
      
      // Cloudflare Images API wraps successful data inside a "result" key
      if (data.success && data.result && data.result.images) {
        setImages(data.result.images);
      } else {
        setImages([]);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    
    try {
      const token = localStorage.getItem('adminToken');
      let completed = 0;
      
      // Upload all files concurrently
      await Promise.all(files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          console.error(`Failed to upload ${file.name}`);
          throw new Error(`Failed to upload ${file.name}`);
        }
        
        completed++;
        setUploadProgress(prev => ({ ...prev, current: completed }));
      }));
      
      // Refresh library after all uploads complete
      fetchMedia();
      showAlert('Success', `Successfully uploaded ${files.length} image(s)!`);
    } catch (error) {
      console.error('Error uploading:', error);
      showAlert('Upload Failed', 'Failed to upload some images. Please check the console for details.');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const executeDelete = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete image');
      
      setImages(prev => prev.filter(img => img.id !== id));
      setSelectedImages(prev => prev.filter(imgId => imgId !== id));
      showAlert('Deleted', 'Image has been successfully deleted.');
    } catch (error) {
      console.error('Error deleting:', error);
      showAlert('Error', 'Failed to delete image from Cloudflare.');
    }
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    showConfirm(
      'Delete Image',
      'Are you sure you want to completely delete this image from Cloudflare? This action is permanent.',
      () => executeDelete(id)
    );
  };

  const toggleSelect = (id) => {
    setSelectedImages(prev => 
      prev.includes(id) ? prev.filter(imgId => imgId !== id) : [...prev, id]
    );
  };

  const executeBulkDelete = async () => {
    setDeleting(true);
    setDeleteProgress({ current: 0, total: selectedImages.length });
    try {
      const token = localStorage.getItem('adminToken');
      let successCount = 0;
      let completedCount = 0;
      
      await Promise.all(selectedImages.map(async (id) => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          console.error(`Failed to delete ${id}`);
        } else {
          successCount++;
        }
        completedCount++;
        setDeleteProgress(prev => ({ ...prev, current: completedCount }));
      }));

      setSelectedImages([]);
      fetchMedia();
      showAlert('Success', `Successfully deleted ${successCount} out of ${selectedImages.length} image(s)!`);
    } catch (error) {
      console.error('Error in bulk delete:', error);
      showAlert('Error', 'Failed to perform bulk delete.');
    } finally {
      setDeleting(false);
      setDeleteProgress({ current: 0, total: 0 });
    }
  };

  const handleBulkDelete = () => {
    if (selectedImages.length === 0) return;
    showConfirm(
      'Bulk Delete',
      `Are you sure you want to permanently delete ${selectedImages.length} image(s)? This action is permanent.`,
      () => executeBulkDelete()
    );
  };

  const copyToClipboard = (e, url) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    showAlert('Success', 'Copied URL to clipboard!');
  };

  // Find the 'public' variant or fallback to the first available variant
  const getDisplayUrl = (variants) => {
    if (!variants || variants.length === 0) return '';
    const publicVariant = variants.find(v => v.endsWith('/public'));
    return publicVariant || variants[0];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="font-clash text-2xl md:text-3xl font-bold text-electric-yellow text-left w-full sm:w-auto">Media Library</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto justify-end">
          {selectedImages.length > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={deleting}
              className="w-full sm:w-auto bg-red-500/20 text-red-500 border border-red-500/50 px-6 py-3 rounded-xl font-bold hover:bg-red-500 hover:text-white transition-colors duration-300 disabled:opacity-50"
            >
              {deleting ? `Deleting ${deleteProgress.current}/${deleteProgress.total}...` : `Delete Selected (${selectedImages.length})`}
            </button>
          )}

          <div className="relative w-full sm:w-auto">
            <input
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              ref={fileInputRef}
              onChange={handleUpload}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full sm:w-auto bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-colors duration-300 disabled:opacity-50 min-w-[160px]"
            >
              {uploading ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` : '+ Upload Image'}
            </button>
          </div>
        </div>
      </div>

      {/* Search Context Block matching AdminGuests */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
        <input
          type="text"
          placeholder="Search images..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 transition-colors"
        />
      </div>

      {/* Main Content */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6 min-h-[60vh]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-electric-yellow text-lg">Loading your media from Cloudflare...</div>
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="bg-midnight/30 rounded-2xl border border-slate-dark/50 p-12 text-center">
            <div className="w-16 h-16 bg-slate-dark/30 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-dark">
              <svg className="w-8 h-8 text-soft-gray" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-warm-white">No matches found</h3>
            <p className="text-soft-gray mt-2">Could not find any images matching "{searchTerm}"</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredImages.map((image, index) => {
              const url = getDisplayUrl(image.variants);
              const isSelected = selectedImages.includes(image.id);
              return (
                <div 
                  key={image.id} 
                  onClick={() => setViewingIndex(index)}
                  className={`group relative bg-midnight/50 rounded-xl overflow-hidden cursor-pointer border transition-all duration-300 ${isSelected ? 'border-electric-yellow ring-2 ring-electric-yellow' : 'border-slate-dark/50 hover:border-electric-yellow/50'}`}
                >
                  <div 
                    onClick={(e) => { e.stopPropagation(); toggleSelect(image.id); }}
                    className={`absolute top-3 left-3 z-20 transition-opacity duration-300 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  >
                    <div className={`w-6 h-6 rounded border flex items-center justify-center ${isSelected ? 'bg-electric-yellow border-electric-yellow text-midnight' : 'bg-midnight/80 border-slate-dark text-transparent hover:border-electric-yellow'}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="aspect-square bg-midnight flex items-center justify-center p-2 relative">
                     <img 
                       src={url} 
                       alt={image.filename} 
                       className={`max-h-full max-w-full object-contain rounded-lg shadow-md transition-opacity duration-300 ${isSelected ? 'opacity-70' : ''}`}
                     />
                     <div className="absolute inset-0 bg-midnight/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300 gap-2 backdrop-blur-sm">
                        <button 
                          onClick={(e) => copyToClipboard(e, url)}
                          className="bg-electric-yellow text-midnight px-3 py-2 rounded-lg font-bold text-sm hover:scale-105 transition-transform"
                        >
                          Copy URL
                        </button>
                        <button 
                          onClick={(e) => handleDelete(e, image.id)}
                          className="bg-red-500 text-white px-3 py-2 rounded-lg font-bold text-sm hover:scale-105 transition-transform"
                        >
                          Delete
                        </button>
                     </div>
                  </div>
                  <div className="p-3 bg-charcoal/80 truncate text-xs text-soft-gray border-t border-slate-dark/50">
                    <p className="truncate font-medium text-warm-white">{image.filename}</p>
                    <p className="mt-1">{new Date(image.createdAt || image.uploaded).toLocaleDateString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox Gallery Modal */}
      {viewingIndex !== null && filteredImages[viewingIndex] && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-midnight/95 backdrop-blur-md transition-opacity duration-300"
          onClick={() => setViewingIndex(null)}
        >
          {/* Close button */}
          <button 
            onClick={(e) => { e.stopPropagation(); setViewingIndex(null); }}
            className="absolute top-6 right-6 text-soft-gray hover:text-white transition-colors z-[70] p-2 hover:bg-white/10 rounded-xl"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>

          {/* Prev Button */}
          <button 
            onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
            className="absolute left-4 lg:left-12 top-1/2 -translate-y-1/2 text-soft-gray hover:text-electric-yellow hover:scale-110 transition-all z-[70] p-3 lg:p-4 bg-black/40 border border-white/10 rounded-full hover:bg-black/80 shadow-2xl"
          >
            <svg className="w-8 h-8 lg:w-10 lg:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
          </button>

          {/* Next Button */}
          <button 
            onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
            className="absolute right-4 lg:right-12 top-1/2 -translate-y-1/2 text-soft-gray hover:text-electric-yellow hover:scale-110 transition-all z-[70] p-3 lg:p-4 bg-black/40 border border-white/10 rounded-full hover:bg-black/80 shadow-2xl"
          >
            <svg className="w-8 h-8 lg:w-10 lg:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
          </button>

          {/* Main Image Container */}
          <div 
            className="relative w-full max-w-7xl max-h-screen p-4 flex flex-col items-center justify-center animate-in fade-in zoom-in duration-300"
            onClick={(e) => e.stopPropagation()} // Prevent click-through closing
          >
            <img 
              src={getDisplayUrl(filteredImages[viewingIndex].variants)} 
              alt={filteredImages[viewingIndex].filename} 
              className="max-w-full max-h-[80vh] min-h-[40vh] object-contain shadow-2xl rounded-sm"
            />
            
            {/* Image Metadata Footer */}
            <div className="mt-6 text-center bg-black/40 backdrop-blur-md border border-white/10 px-8 py-4 rounded-2xl shadow-2xl">
                <p className="text-electric-yellow font-bold text-lg">{filteredImages[viewingIndex].filename}</p>
                <div className="flex items-center justify-center gap-4 mt-2">
                  <span className="text-soft-gray font-medium">{viewingIndex + 1} of {filteredImages.length}</span>
                  <span className="w-1 h-1 rounded-full bg-soft-gray/50"></span>
                  <span className="text-soft-gray">{new Date(filteredImages[viewingIndex].createdAt || filteredImages[viewingIndex].uploaded).toLocaleDateString()}</span>
                  <span className="w-1 h-1 rounded-full bg-soft-gray/50"></span>
                  <button 
                    onClick={(e) => copyToClipboard(e, getDisplayUrl(filteredImages[viewingIndex].variants))}
                    className="text-electric-yellow hover:text-white transition-colors underline text-sm"
                  >
                    Copy URL
                  </button>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Dialog Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-center gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Delete' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
